import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useRouterState } from "@tanstack/react-router";
import { ptDict } from "./dict.pt";
import { getCachedDict, loadDict, type Dict } from "./dictionaries";
import { getLocaleFromPathname, type Locale, DEFAULT_LOCALE } from "./types";

interface LocaleContextValue {
  locale: Locale;
  t: Dict;
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: DEFAULT_LOCALE,
  t: ptDict,
});

/**
 * Deep-merge de objetos planos preservando shape do "base" (PT-BR).
 * Sempre que uma chave estiver ausente, vazia ou nula no override (EN/ES),
 * o valor cai para o PT-BR, garantindo que nada renderize "undefined"
 * nem misture idiomas inesperadamente.
 */
function mergeWithFallback<T>(base: T, override: T | undefined): T {
  if (override === undefined || override === null) return base;
  if (Array.isArray(base)) {
    return (Array.isArray(override) && override.length > 0 ? override : base) as T;
  }
  if (typeof base === "object" && base !== null && typeof override === "object") {
    const out: Record<string, unknown> = { ...(base as Record<string, unknown>) };
    for (const key of Object.keys(base as Record<string, unknown>)) {
      const b = (base as Record<string, unknown>)[key];
      const o = (override as Record<string, unknown>)[key];
      out[key] = mergeWithFallback(b as unknown, o as unknown);
    }
    return out as T;
  }
  // primitivos: aceita override apenas se for string não-vazia / valor truthy
  if (typeof base === "string") {
    return (typeof override === "string" && override.trim().length > 0 ? override : base) as T;
  }
  return (override ?? base) as T;
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Fallback duro: qualquer pathname que não comece com /en ou /es vira pt-BR.
  const locale = getLocaleFromPathname(pathname);

  // pt-BR é síncrono (bundle crítico). EN/ES chegam por import() dinâmico:
  // até o chunk resolver, renderizamos com o dicionário pt como base — o
  // mergeWithFallback já garante que nada apareça vazio nesse intervalo.
  const [override, setOverride] = useState<Dict | undefined>(() =>
    locale === DEFAULT_LOCALE ? undefined : getCachedDict(locale),
  );

  useEffect(() => {
    if (locale === DEFAULT_LOCALE) {
      setOverride(undefined);
      return;
    }
    const cached = getCachedDict(locale);
    if (cached) {
      setOverride(cached);
      return;
    }
    let alive = true;
    loadDict(locale).then((d) => {
      if (alive) setOverride(d);
    });
    return () => {
      alive = false;
    };
  }, [locale]);

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, t: mergeWithFallback(ptDict, override) }),
    [locale, override],
  );
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): Locale {
  return useContext(LocaleContext).locale;
}

export function useT(): Dict {
  return useContext(LocaleContext).t;
}
